import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'

import { logout, getImpersonationStatus, endImpersonation } from '@/api/actions/auth/authActions'
import { clearAllTokens } from '@/utils/auth/tokenManager'
import { clearImpersonationFlag } from '@/utils/auth/impersonationSession'
import { clearAuth } from '@/store/slices/authSlice'
import { Mutations } from '@/api/mutations.enum'
import { NavigationRoutes } from '@/router/navigation.types'

export const useLogout = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const mutation = useMutation<void, AxiosError, void>({
        mutationKey: [Mutations.logout],
        mutationFn: async () => {
            // If this is an impersonation session, end it via backend first
            try {
                const status = await getImpersonationStatus()
                if (status?.is_impersonating) {
                    await endImpersonation()
                    return
                }
            } catch {
                // If status fails, fall through to regular logout
            }
            await logout()
        },
        onSuccess: () => {
            clearAllTokens()
            clearImpersonationFlag()
            dispatch(clearAuth())
            queryClient.clear()
            // Clear onboarding check flag
            sessionStorage.removeItem('checkOnboarding')
            // Don't show toast here - let asyncToast handle it
            navigate(NavigationRoutes.LOGIN, { replace: true })
        },
        onError: (error: AxiosError) => {
            if (error.response?.status === 401) {
                // Session expired - clear everything
                clearAllTokens()
                clearImpersonationFlag()
                dispatch(clearAuth())
                queryClient.clear()
                // Clear onboarding check flag
                sessionStorage.removeItem('checkOnboarding')
                // Don't show toast here - let asyncToast handle it
                navigate(NavigationRoutes.LOGIN, { replace: true })
            }
        },
    })

    return mutation
}
